import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginUser } from '../../features/auth/authThunks';
import { clearError } from '../../features/auth/authSlice';
import {
  clearSessionEndReason,
  readSessionEndReason,
  SESSION_END_REASON,
} from '../../features/auth/sessionEndReason';
import { Package, Lock, Mail, AlertCircle, Clock } from 'lucide-react';

export const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state) => state.auth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Read, not consumed: the page that hit the 401 renders this screen once
  // before the reload lands, and clearing here would leave the reloaded one
  // nothing to say. It is cleared when the user signs in again.
  const [sessionExpired] = useState(
    () => readSessionEndReason() === SESSION_END_REASON.EXPIRED
  );

  // Limpiar errores si el usuario empieza a escribir de nuevo
  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [email, password, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !password) return;

    clearSessionEndReason();
    dispatch(loginUser({ email, password, navigate }));
  };

  return (
    <div className="min-h-screen bg-ninja-gray dark:bg-[#0f0f0f] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-xl overflow-hidden border border-border">
        {/* Header */}
        {/* The same surface the sidebar's own header sits on, so the wordmark
            can use its very token and read the same in both themes. */}
        <div className="bg-white dark:bg-[#1a1a1a] border-b border-border p-8 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-ninja-green rounded-full blur-[80px] opacity-40 translate-x-1/2 -translate-y-1/2" />

          <div className="relative z-10 flex flex-col items-center">
            <div className="w-16 h-16 bg-ninja-greenLight backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 border border-ninja-green/20 shadow-lg">
              <Package size={32} className="text-ninja-green" />
            </div>
            <h1 className="text-2xl font-extrabold text-ninja-green tracking-tight">Ninja{' '}<span className="text-ninja-dark dark:text-white">Hubs</span></h1>
            <p className="text-sm text-text-secondary mt-2 font-medium">Torre de control logística</p>
          </div>
        </div>

        {/* Form */}
        <div className="p-8">
          <h2 className="text-xl font-bold text-ninja-dark dark:text-white mb-6">Iniciar Sesión</h2>

          {sessionExpired && !error && (
            <div
              data-testid="session-expired-notice"
              className="mb-6 p-4 rounded-xl border border-border flex items-start gap-3"
            >
              <Clock size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <p className="text-sm text-text-secondary font-medium">
                Tu sesión expiró. Vuelve a entrar para seguir donde estabas.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 rounded-xl flex items-start gap-3">
              <AlertCircle size={20} className="text-danger flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger font-medium">
                {typeof error === 'string' ? error : 'Credenciales Inválidas'}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-text-secondary block">Correo electrónico</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-text-muted">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-ninja-gray dark:bg-[#2d2d2d] border border-border rounded-xl py-2.5 pl-10 pr-4 text-ninja-dark dark:text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ninja-green focus:border-transparent transition-all"
                  placeholder="admin@ninja.hubs"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              {/* No "¿Olvidaste tu contraseña?" here: it was an `href="#"`
                  that went nowhere, and a dead link on a login form reads as a
                  broken product to the one person who most needs it to work.
                  It comes back with the flow behind it. */}
              <label className="text-sm font-semibold text-text-secondary block">Contraseña</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-text-muted">
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-ninja-gray dark:bg-[#2d2d2d] border border-border rounded-xl py-2.5 pl-10 pr-4 text-ninja-dark dark:text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ninja-green focus:border-transparent transition-all"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-ninja-green hover:bg-[#00c088] text-white py-3 rounded-xl font-bold text-sm shadow-md transition-all flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed mt-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Entrar al panel'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Background Decor */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-ninja-greenLight blur-[120px] opacity-50" />
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-ninja-greenLight blur-[120px] opacity-30" />
      </div>
    </div>
  );
};
